import { useState, useEffect } from 'react';
import { useGameStore } from '../stores/gameStore';
import type { ClientMessage } from '@wasted-games/shared';
import CountdownTimer from './CountdownTimer';

interface VotingProps {
  send: (msg: ClientMessage) => void;
}

function Voting({ send }: VotingProps) {
  const [selected, setSelected] = useState<string | null>(null);

  const phase = useGameStore((s) => s.phase);
  const playerId = useGameStore((s) => s.playerId);
  const submissions = useGameStore((s) => s.submissions);
  const config = useGameStore((s) => s.config);
  const phaseStartTime = useGameStore((s) => s.phaseStartTime);
  const hasVotedBoring = useGameStore((s) => s.hasVotedBoring);
  const hasVotedExciting = useGameStore((s) => s.hasVotedExciting);
  const myBoringVote = useGameStore((s) => s.myBoringVote);
  const currentRound = useGameStore((s) => s.currentRound);

  const isBoring = phase === 'vote_boring';
  const hasVoted = isBoring ? hasVotedBoring : hasVotedExciting;

  // Reset selection when phase changes
  useEffect(() => {
    setSelected(null);
  }, [phase]);

  // Submissions are keyed by submissionId during voting (anonymous)
  const submissionList = Object.values(submissions).sort((a, b) =>
    a.submissionId.localeCompare(b.submissionId)
  );

  const handleVote = () => {
    if (!selected) return;

    send({
      type: 'action',
      action: {
        type: isBoring ? 'vote_boring' : 'vote_exciting',
        gameType: 'gambit',
        targetSubmissionId: selected,
      },
    });

    setSelected(null);
  };

  const isOwnSubmission = (sub: { playerId: string }) => {
    // During voting, server sends playerId as 'anonymous' but we can check
    // if this player has a submission entry in the store keyed by their playerId
    return sub.playerId === playerId;
  };

  const isDisabled = (sub: { submissionId: string; playerId: string }) => {
    if (isOwnSubmission(sub)) return true;
    if (!isBoring && myBoringVote === sub.submissionId) return true;
    return false;
  };

  const getDisabledReason = (sub: { submissionId: string; playerId: string }) => {
    if (isOwnSubmission(sub)) return 'Your dare';
    if (!isBoring && myBoringVote === sub.submissionId) return 'Voted boring';
    return null;
  };

  if (hasVoted) {
    return (
      <div className="min-h-screen flex flex-col items-center justify-center px-4">
        <div className="text-center max-w-md">
          <div className="text-5xl mb-4">{isBoring ? '\u{1F634}' : '\u{1F525}'}</div>
          <h2 className="text-2xl font-bold mb-2">Vote Cast!</h2>
          <p className="text-base-content/60">Waiting for other players...</p>

          {config.timeLimitVoting && phaseStartTime && (
            <div className="mt-6 flex justify-center">
              <CountdownTimer startTime={phaseStartTime} duration={config.timeLimitVoting} />
            </div>
          )}
        </div>
      </div>
    );
  }

  return (
    <div className="min-h-screen flex flex-col px-4 py-6">
      <div className="max-w-md w-full mx-auto space-y-4 flex-1">
        <div className="text-center">
          <div className="badge badge-primary badge-outline mb-2">Round {currentRound}</div>
          <h2 className="text-2xl font-bold mb-1">
            Vote for the{' '}
            <span className={isBoring ? 'text-warning' : 'text-success'}>
              {isBoring ? 'MOST BORING' : 'MOST EXCITING'}
            </span>{' '}
            dare
          </h2>
          <p className="text-base-content/50 text-sm">
            {isBoring
              ? 'The loser has to do their dare!'
              : 'The winner gets a point!'}
          </p>
        </div>

        {config.timeLimitVoting && phaseStartTime && (
          <div className="flex justify-center">
            <CountdownTimer startTime={phaseStartTime} duration={config.timeLimitVoting} />
          </div>
        )}

        <div className="space-y-3">
          {submissionList.map((sub) => {
            const disabled = isDisabled(sub);
            const reason = getDisabledReason(sub);
            const isSelected = selected === sub.submissionId;

            return (
              <button
                key={sub.submissionId}
                onClick={() => !disabled && setSelected(sub.submissionId)}
                disabled={disabled}
                className={`card w-full text-left transition-all ${
                  isSelected
                    ? isBoring
                      ? 'bg-warning/20 ring-2 ring-warning'
                      : 'bg-success/20 ring-2 ring-success'
                    : disabled
                    ? 'bg-base-300 opacity-40'
                    : 'bg-base-200 hover:bg-base-300 active:scale-[0.98]'
                }`}
              >
                <div className="card-body py-4 px-4">
                  <div className="flex items-start gap-3">
                    <span className="badge badge-lg badge-neutral font-mono shrink-0 text-lg">
                      {sub.submissionId}
                    </span>
                    <div className="flex-1 min-w-0">
                      <p className="text-base leading-relaxed">{sub.dare}</p>
                      {reason && (
                        <p className="text-xs text-base-content/40 mt-1">{reason}</p>
                      )}
                    </div>
                    {isSelected && (
                      <span className={`text-2xl ${isBoring ? 'text-warning' : 'text-success'}`}>
                        {'\u2713'}
                      </span>
                    )}
                  </div>
                </div>
              </button>
            );
          })}
        </div>

        <div className="sticky bottom-4 pt-4">
          <button
            onClick={handleVote}
            disabled={!selected}
            className={`btn btn-lg w-full ${
              isBoring ? 'btn-warning' : 'btn-success'
            }`}
          >
            {isBoring ? '\u{1F634} Vote Most Boring' : '\u{1F525} Vote Most Exciting'}
          </button>
        </div>
      </div>
    </div>
  );
}

export default Voting;
